import React, {useEffect, useRef, useState} from 'react';
import { useLocation } from 'react-router-dom';
import { useLoading } from '../../context';
import {
  HeroLogo,
  Footer,
  Button,
  Label,
  TextInput,
  FormItemContainer,
  FormContainer,
  SingleLineFormItemWrapper,
} from '../../components';
import CreditCardPaymentImage from '../../resource/figma-images/credit-card-payment-image.svg';
import { Alert, Stack, styled } from '@mui/material';
import ReCAPTCHA from "react-google-recaptcha";
import {
  getCreditCardPaymentImage,
  getDefaultPaymentMethod,
  getPageHeroLogo,
  getPageTitle,
  getThemeColor,
  shouldVerifyRecaptcha
} from "../../customHostConfigOnClient";
import {
  PaymentDetails,
  PaymentHiddenWrapper,
  PaymentOption,
  PaymentOptionImage,
  PaymentOptionList,
  PaymentOptionText,
} from "../../components/PaymentMethod";
import {useRecaptchaReady, verifyRecaptcha} from "../../utils/useRecaptchaReady";
import {
  PAYMENT_METHOD_FISERV_IPG,
  PAYMENT_METHOD_INITIAL
} from "../constants";
import {PrivacyPolicy} from "../../components/PrivacyPolicy";
import {FiservIpgCardPayment} from "./FiservIpgCardPayment";


const InfoMessage = styled('div')`
  color: darkgray;
  text-align: center;
`;

const PaymentFormPageTitle = styled('div')`
  font-size: 15px;
  text-align: center;
  font-weight: 500;
  color: #747F94;
`;


export const SarafiHaidariDandenongPaymentForm = () => {
  const { showLoading, hideLoading } = useLoading();

  // Hide /sarafi-dandenong-pay page for other domains.
  if (window.location.hostname !== 'sarafi-dandenong.oligo.com.au' && window.location.hostname !== 'localhost') {
    window.location = '/' + window.location.search;
  }

  const searchParams = new URLSearchParams(useLocation().search);
  const [billAmount, setBillAmount] = useState(searchParams.get('XeroAmount') || '');
  const [billAmountDisabled, setBillAmountDisabled] = useState(false);
  const [userEmail, setUserEmail] = useState(searchParams.get('Email') || '');
  const [error, setError] = useState('');
  const [paymentResult, setPaymentResult] = useState('');
  const [billAmountError, setBillAmountError] = useState(false);
  const [paymentReference, setPaymentReference] = useState(searchParams.get('XeroInvoiceNo') || '');
  const [paymentReferenceError, setPaymentReferenceError] = useState(false);
  const [familyName, setFamilyName] = useState('');
  const [familyNameError, setFamilyNameError] = useState(false);
  const [userContactPhone, setUserContactPhone] = useState('');
  const fiservIpgCardRef = useRef(null);
  const recaptchaRef = useRef(null);
  const recaptchaReady = useRecaptchaReady();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(getDefaultPaymentMethod() || PAYMENT_METHOD_INITIAL);

  useEffect(() => {
    if (paymentResult !== '') setSelectedPaymentMethod(PAYMENT_METHOD_INITIAL);
  }, [paymentResult]);

  const handlePaymentMethodChange =
    (paymentMethod) => () => {
      setSelectedPaymentMethod(prev => (prev !== paymentMethod ? paymentMethod : prev));
    };

  const handleBillAmountChange = (value) => {
    if (isValidFloat(value)) {
      setBillAmount(value);
      setBillAmountError(false);
    }
  };

  const handlePaymentReferenceChange = (value) => {
    setPaymentReference(value);
    setPaymentReferenceError(false);
  };

  const handleFamilyNameChange = (value) => {
    setFamilyName(value);
    setFamilyNameError(false);
  };


  const isValidFloat = (value) => {
    return /^-?\d*\.?\d*$/.test(value);
  };

  const createPaymentOrderAndNavigate = async () => {
    if (!billAmount || isNaN(parseFloat(billAmount)) || parseFloat(billAmount) <= 0) {
      setBillAmountError(true);
      return;
    }

    if (!paymentReference) {
      setPaymentReferenceError(true);
      return;
    }

    if (!familyName) {
      setFamilyNameError(true);
      return;
    }

    setBillAmountDisabled(true);

    if (selectedPaymentMethod === PAYMENT_METHOD_INITIAL) {
      setError('Please select a payment method.');
      return;
    }

    if (shouldVerifyRecaptcha() && recaptchaReady && recaptchaRef.current) {
      // Validate ReCAPTCHA
      await verifyRecaptcha(recaptchaRef, setError);
    }

    try {
      if (selectedPaymentMethod === PAYMENT_METHOD_FISERV_IPG) {
        await fiservIpgCardRef.current.paymentSubmit(setPaymentResult);
      } else {
        showLoading();
      }
    } catch (err) {
      hideLoading();
      setError('Create payment order failed, please try again.');
    }
  };

  const handlePayBill = async () => {
    try {
      await createPaymentOrderAndNavigate();
    } catch (err) {
      setError('Payment validation request sending failed');
      console.error(err);
    }
  };

  return (
    <FormContainer>
      <HeroLogo logo={getPageHeroLogo()} redirectPath='/sarafi-dandenong-pay' />
      <Stack gap="20px" alignItems="center">
        <PaymentFormPageTitle>{ getPageTitle() }</PaymentFormPageTitle>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack gap="20px" width="100%">
          <SingleLineFormItemWrapper>
            <FormItemContainer marginRight={'10px'}>
              <Label isRequired>Customer Name</Label>
              <TextInput
                placeholder=""
                value={familyName || ''}
                onChange={handleFamilyNameChange}
                error={familyNameError}
              />
            </FormItemContainer>
            <FormItemContainer>
              <Label isRequired>Payment Reference</Label>
              <TextInput
                placeholder=""
                value={paymentReference || ''}
                onChange={handlePaymentReferenceChange}
                error={paymentReferenceError}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>
          <SingleLineFormItemWrapper>
            <FormItemContainer marginRight={'10px'}>
              <Label isRequired>Payment Amount</Label>
              <TextInput
                placeholder="$"
                value={billAmount || ''}
                onChange={handleBillAmountChange}
                error={billAmountError}
                disabled={billAmountDisabled}
              />
            </FormItemContainer>
            <FormItemContainer>
              <Label>Contact Telephone No.</Label>
              <TextInput
                placeholder=""
                value={userContactPhone || ''}
                onChange={setUserContactPhone}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>
          <SingleLineFormItemWrapper>
            <FormItemContainer>
              <Label>Receipt Email</Label>
              <TextInput
                placeholder=""
                value={userEmail || ''}
                onChange={setUserEmail}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>

          <div>
            <Label>Payment Method</Label>
            { !getDefaultPaymentMethod() && <div style={{color: 'rgba(84, 84, 84, 0.56)', marginBottom: '15px', fontSize: '13px'}}>Please select a payment method.</div> }

            <PaymentOptionList>
              <PaymentOption
                onClick={handlePaymentMethodChange(PAYMENT_METHOD_FISERV_IPG)}
                selected={selectedPaymentMethod === PAYMENT_METHOD_FISERV_IPG}
                borderColor={getThemeColor()}
              >
                <PaymentOptionImage src={CreditCardPaymentImage} />
                <PaymentOptionText>Credit or Debit Cards</PaymentOptionText>
              </PaymentOption>
            </PaymentOptionList>

            <PaymentDetails>
              <PaymentHiddenWrapper show={selectedPaymentMethod === PAYMENT_METHOD_FISERV_IPG}>
                <FiservIpgCardPayment
                  ref={fiservIpgCardRef}
                  billAmount={billAmount}
                  billReference={paymentReference}
                  userEmail={userEmail}
                  creditCardImage={getCreditCardPaymentImage()}
                  customerName={familyName}
                  contactPhone={userContactPhone}
                />
              </PaymentHiddenWrapper>
            </PaymentDetails>

            { paymentResult && <InfoMessage>{paymentResult}</InfoMessage> }

            { shouldVerifyRecaptcha() && <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6LcWNAUrAAAAAHX2haltRqZqMAMZ0qB29zv16EB8"
              size="invisible"
            /> }

            <Button $themeColor={getThemeColor()} onClick={handlePayBill} block>
              Continue to pay
            </Button>
          </div>

        </Stack>
      </Stack>
      <PrivacyPolicy />
      <Footer />
    </FormContainer>
  );
};
